import Loader from "atoms/loaders/loader";
import HitRecord from "models/view-models/search/hit-record";
import SearchResultCard from "molecules/search-result-card/search-result-card";
import React, { useCallback, useState } from "react";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import FilterButton from "organisms/filter-button/filter-button";
import { useLocalization } from "utilities/hooks/use-localization";
import NoResultsBanner from "molecules/banners/no-results-banner";
import { AriaRole } from "utilities/enumerations/aria-role";
import { GoogleAnalyticsIds } from "constants/google-analytics-ids";
import Button from "atoms/buttons/button";
import PublicationSelectionModal from "organisms/modals/publication-selection-modal/publication-selection-modal";
import useModalActions from "utilities/hooks/use-modal-actions";
import usePublicationFavorites from "utilities/hooks/domain/user-publication-favorites/use-publication-favorites";
import UserPublicationFavoriteRecord from "models/view-models/user-publication-favorite-record";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import SelectFormField from "molecules/form-fields/select-form-field";
import { SelectOption } from "atoms/forms/select";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useUserSettings from "utilities/hooks/domain/user-settings/use-user-settings";
import UserSettingRecord from "models/view-models/user-setting-record";
import GlobalStateRecord from "models/view-models/global-state-record";
import NotificationDotIcon from "atoms/icons/notification-dot-icon";
import { Translator } from "utilities/types/translator-type";
import CultureResources from "utilities/interfaces/culture-resources";
import SearchSortOrderBy from "models/enumerations/sorting/search-results/search-sort-order-by";
import { UserSettingsKeys } from "constants/user-settings-keys";
import { SearchProvider } from "models/enumerations/search-provider";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface SearchModalResultsProps {
    filterCount: number;
    loading: boolean;
    onFilterModalOpen: () => void;
    onResultClick: () => void;
    results?: Array<HitRecord>;
    resultTotal: number;
    searchText: string;
    isSearchingAllPublications?: boolean;
    setSortOrder: (value: number) => void;
    sortOrder?: SearchSortOrderBy;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-search-modal-results";
const HEADER_CLASS = `${BASE_CLASS}__header`;

const SearchResultsSortOrderSelectOptions = (
    t: Translator<CultureResources>
): Array<SelectOption<SearchSortOrderBy, number>> => {
    return [
        {
            label: t("sortByAppearance"),
            value: SearchSortOrderBy.PrintOrder,
        },
        {
            label: t("sortByRelevance"),
            value: SearchSortOrderBy.Relevance,
        },
    ];
};

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const SearchModalResults: React.FC<SearchModalResultsProps> = React.memo(
    (props: SearchModalResultsProps) => {
        const {
            filterCount,
            results,
            resultTotal,
            loading,
            onFilterModalOpen,
            onResultClick,
            searchText,
            isSearchingAllPublications,
            setSortOrder,
            sortOrder,
        } = props;
        const { t } = useLocalization();
        const isMobile = useBreakpoint(
            Breakpoints.Phone,
            BreakpointComparer.MaxWidth
        );
        const { globalState, setGlobalState } = useGlobalState();
        const userDisplayOrderSetting = globalState.getUserSetting(
            "SearchResultsSortBy"
        );
        const searchProviderSetting =
            globalState.getUserSettingValue(
                UserSettingsKeys.SearchProviderName
            ) ?? SearchProvider.AmazonCloudSearchProvider;
        const [isLoading, setLoading] = useState<boolean>(false);
        const currentUserId = globalState.currentIdentity?.userId();
        const hasSearchResults = (results?.length ?? 0) > 0;
        const { useSearchResultsOrderSelection, useSparkySearch } =
            useFeatureFlags();

        const selectSortOptions = SearchResultsSortOrderSelectOptions(t);

        const initialSortOrder = () => {
            const value = sortOrder ?? SearchSortOrderBy.Relevance;
            return (
                selectSortOptions.find((option) => option.value === value) ??
                selectSortOptions[1]
            );
        };

        const showSearchResultsOrderSelection =
            useSearchResultsOrderSelection &&
            useSparkySearch &&
            searchProviderSetting === SearchProvider.NfpaAiSearchProviderV2;

        const [searchResultsOrder, setSearchResultsOrder] =
            useState<SelectOption<SearchSortOrderBy, number>>(initialSortOrder);

        const { createOrUpdate: createOrUpdateUserSettings } =
            useUserSettings();
        const {
            isOpen: isPublicationSelectionModalOpen,
            handleOpen: handlePublicationSelectionModalOpen,
            handleClose: handlePublicationSelectionModalClose,
        } = useModalActions();
        const {
            favorites,
            setFavorites,
            refresh: refreshFavorites,
        } = usePublicationFavorites();
        const setAndRefreshFavorites = useCallback(
            (favorites: Array<UserPublicationFavoriteRecord>): void => {
                setFavorites(favorites);
                refreshFavorites();
            },
            [refreshFavorites, setFavorites]
        );

        const handleChangeSearchResultsOrder = (
            option: SelectOption<SearchSortOrderBy, number> | undefined
        ) => {
            if (!option) {
                return;
            }
            if (option.value === searchResultsOrder.value) {
                return;
            }
            setSearchResultsOrder(option);
            if (option) {
                setSortOrder(option.value);
                handleSettingToggle(
                    option.value.toString(),
                    userDisplayOrderSetting
                );
            }
        };

        const handleSettingToggle = async (
            value: string,
            setting?: UserSettingRecord
        ) => {
            if (currentUserId == null || isLoading || value == null) {
                return;
            }

            setLoading(true);

            const dto = setting?.with({
                value: value,
            });

            const result = await createOrUpdateUserSettings(dto, currentUserId);

            if (result == null) {
                setLoading(false);
                return;
            }

            setGlobalState((globalState: GlobalStateRecord) =>
                globalState.updateUserSetting(result)
            );
            setLoading(false);
        };

        return (
            <div className={BASE_CLASS}>
                {isMobile && (
                    <div className={HEADER_CLASS}>
                        <span className={`${HEADER_CLASS}__count`}>
                            {t("result", { count: resultTotal })}
                        </span>
                        <FilterButton
                            count={filterCount}
                            onClick={onFilterModalOpen}>
                            {t("filter", { count: filterCount })}
                        </FilterButton>
                    </div>
                )}
                <div
                    aria-label={t("searchModal-SearchResults")}
                    className={`${BASE_CLASS}__list`}
                    role={AriaRole.List}>
                    {loading && <Loader />}
                    <div className={`${BASE_CLASS}__search-result-actions`}>
                        {!loading && searchText && (
                            <div
                                className={`${BASE_CLASS}__search-result-actions__matching-code-and-standards`}>
                                <span
                                    className={`${BASE_CLASS}__search-result-actions__matching-code-and-standards__label`}>
                                    {t("searchModal-MatchingCodesAndStandards")}
                                </span>
                                <span
                                    className={`${BASE_CLASS}__search-result-actions__matching-code-and-standards__button`}>
                                    <Button
                                        size={ButtonSizes.Small}
                                        style={ButtonStyles.Tertiary}
                                        onClick={
                                            handlePublicationSelectionModalOpen
                                        }>
                                        {t(
                                            "searchModal-FindMatchingPublications"
                                        )}
                                    </Button>
                                </span>
                            </div>
                        )}
                        {!isSearchingAllPublications &&
                        showSearchResultsOrderSelection ? (
                            <>
                                <SelectFormField
                                    cssClassName={`${BASE_CLASS}__search-result-actions__select-search-order`}
                                    options={SearchResultsSortOrderSelectOptions(
                                        t
                                    )}
                                    onChange={(option) =>
                                        handleChangeSearchResultsOrder(option)
                                    }
                                    value={searchResultsOrder.value}
                                    aria-label={t(
                                        "searchModal-sortOrderDropdown-label"
                                    )}
                                    label=""
                                />
                                {userDisplayOrderSetting?.userId !==
                                    currentUserId && (
                                    <div
                                        className={`${BASE_CLASS}__search-result-actions__notification-dot`}>
                                        <NotificationDotIcon pulse={true} />
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>
                    {!loading && !hasSearchResults && (
                        <NoResultsBanner
                            id={GoogleAnalyticsIds.SEARCH_MODAL_NO_RESULTS}
                            subtitle={t("noSearchResultsBanner-subTitle")}
                            title={t("noSearchResultsBanner-title")}
                        />
                    )}
                    {isPublicationSelectionModalOpen && (
                        <PublicationSelectionModal
                            closeDialog={handlePublicationSelectionModalClose}
                            favorites={favorites}
                            isVisible={isPublicationSelectionModalOpen}
                            onChangeFavorites={setAndRefreshFavorites}
                            initialSearchText={searchText}
                            onResultClick={onResultClick}
                        />
                    )}
                    {!loading &&
                        results?.map((hit: HitRecord, index: number) => (
                            <SearchResultCard
                                dataTestId={`search--search-modal-results--anchor-${index}`}
                                closeDialog={onResultClick}
                                hit={hit}
                                id={GoogleAnalyticsIds.SEARCH_RESULT_CARD}
                                index={index}
                                key={hit.getKey()}
                                onClick={() => null}
                            />
                        ))}
                </div>
            </div>
        );
    }
);
// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default SearchModalResults;

// #endregion Exports
